import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ChatStateService } from '../../core/services/chat-state.service';
import { Message, Session } from '../../core/models/chat.models';
// Adjusted path from layouts/chat to pages/chat/components
import { DeleteConfirmationModalComponent } from '../../pages/chat/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { SidebarComponent } from '../../pages/chat/components/sidebar/sidebar.component';
import { NotificationBannersComponent } from '../../pages/chat/components/notification-banners/notification-banners.component';

@Component({
  selector: 'app-chat-layout',
  imports: [
    CommonModule,
    RouterModule,
    DeleteConfirmationModalComponent,
    SidebarComponent,
    NotificationBannersComponent
  ],
  templateUrl: './chat-layout.component.html',
  styleUrls: ['./chat-layout.component.scss']
})
export class ChatLayoutComponent implements OnInit, OnDestroy {

  activeId: string | null = null;
  sessions: Session[] = [];
  isLoading = false;
  
  isDeleteModalOpen = false;
  sessionToDelete: Session | null = null;
  openMenuId: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    public chatState: ChatStateService,
    private apiService: ApiService
  ) { }

  ngOnInit(): void {
    this.chatState.id$.pipe(takeUntil(this.destroy$)).subscribe(
      id => this.activeId = id
    );

    this.chatState.sessions$.pipe(takeUntil(this.destroy$)).subscribe(
      sessions => this.sessions = sessions
    );

    this.chatState.isLoading$.pipe(takeUntil(this.destroy$)).subscribe(
      loading => this.isLoading = loading
    );

    this.loadSessions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSessions(): void {
    this.apiService.getSessions().subscribe({
      next: sessions => this.chatState.setSessions(sessions),
      error: err => this.handleError(err, 'Failed to load chat sessions')
    });
  }

  handleNewSession(): void {
    this.chatState.clearMessages('new');
    this.chatState.setLoading('new', false);
    this.chatState.setSessionId(null);
    this.chatState.clearNotifications();
  }

  onSessionSelected(session: Session): void {
    if (session.id === this.activeId) return;
 
    this.chatState.setSessionId(session.id);
    this.chatState.clearNotifications();
 
    const existingMessages = this.chatState.getMessagesForSession(session.id);
    const isSessionCurrentlyLoading = this.chatState.getLoadingForSession(session.id);
 
    if (existingMessages.length === 0 && !isSessionCurrentlyLoading) {
      this.chatState.setLoading(session.id, true);
      this.apiService.getSessionMessages(session.id).subscribe({
        next: response => {
          const formattedMessages = (response.messages || []).map(msg => {
            if (msg.role === 'agent') {
              try {
                // If content is a JSON string (typical for agent responses), parse it into 'data'
                msg.data = JSON.parse(msg.content || '{}');
              } catch (e) {
                msg.data = { answer: msg.content || '' };
              }
            }
            return msg;
          });
 
          this.chatState.setMessages(session.id, formattedMessages);
          this.chatState.setLoading(session.id, false);
        },
        error: (err: any) => {
          this.handleError(err, 'Failed to load session messages');
          this.chatState.setLoading(session.id, false);
        }
      });
    }
  }

  onDeleteSession(session: Session): void {
    if (confirm(`Are you sure you want to delete "${session.title}"?`)) {
      this.apiService.deleteSession(session.id).subscribe({
        next: () => {
          this.chatState.removeSession(session.id);
          if (this.activeId === session.id) {
            this.chatState.setSessionId(null);
          }
        },
        error: (err) => this.handleError(err, 'Failed to delete session')
      });
    }
  }

  toggleMenu(event: Event | null, id: string): void {
    if (event) event.stopPropagation();
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  handleDeleteClick(event: Event | null, session: Session): void {
    if (event) event.stopPropagation();
    this.sessionToDelete = session;
    this.isDeleteModalOpen = true;
    this.openMenuId = null;
  }

  confirmDelete(): void {
    if (this.sessionToDelete) {
      this.onDeleteSession(this.sessionToDelete);
    }
    this.isDeleteModalOpen = false;
    this.sessionToDelete = null;
  }

  cancelDelete(): void {
    this.isDeleteModalOpen = false;
    this.sessionToDelete = null;
  }

  private handleError(err: any, contextMessage: string): void {
    console.error(contextMessage, err);
    const errorMessage = err?.message || err?.error || 'Unknown error';
    this.chatState.setError(`${contextMessage}: ${errorMessage}`);
  }
}
